import { useEffect, useState } from "react";
import { Activity, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, fmtTime } from "@/lib/api";
import { Button } from "@/components/ui/button";

// Small operational-visibility card for the signal engine.
// Reads fields from /api/bot/status → runtime.* (no new APIs).
// Color state:
//   green  — healthy (running + fresh heartbeat + fast cycles)
//   yellow — stale / blocked / slow cycle / sleeping
//   red    — crashed / last_error present

function stateMeta({ engineState, cycleMs, heartbeatAgeS, lastError }) {
  // red: crashed or explicit error
  if (engineState === "crashed" || engineState === "blocked") {
    return { tone: "red", label: engineState === "blocked" ? "BLOCKED" : "CRASHED" };
  }
  // yellow: stale heartbeat (> 2min) or slow cycles (> 3s)
  if (heartbeatAgeS > 120) {
    return { tone: "yellow", label: "STALE" };
  }
  if (cycleMs && cycleMs > 3000) {
    return { tone: "yellow", label: "SLOW" };
  }
  if (engineState === "sleeping") {
    return { tone: "yellow", label: "SLEEPING" };
  }
  if (engineState === "running") {
    return { tone: "green", label: "HEALTHY" };
  }
  // fallback — unknown state or engine hasn't cycled yet
  if (lastError) {
    return { tone: "red", label: "ERROR" };
  }
  return { tone: "yellow", label: (engineState || "IDLE").toUpperCase() };
}

const TONE_CLASS = {
  green: { dot: "bg-[#00E676]", border: "border-[#00E676]/30", text: "text-[#00E676]", bg: "bg-[#00E676]/5" },
  yellow: { dot: "bg-[#F59E0B]", border: "border-[#F59E0B]/30", text: "text-[#F59E0B]", bg: "bg-[#F59E0B]/5" },
  red: { dot: "bg-[#FF3B30]", border: "border-[#FF3B30]/40", text: "text-[#FF3B30]", bg: "bg-[#FF3B30]/5" },
};

export default function EngineHealthCard() {
  const [runtime, setRuntime] = useState(null);
  const [restarting, setRestarting] = useState(false);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const s = await api.status();
        if (alive) setRuntime(s?.runtime || {});
      } catch {}
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const restart = async () => {
    setRestarting(true);
    try {
      await api.signalEngineRestart();
      toast.success("Signal engine restart requested");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setRestarting(false);
    }
  };

  if (!runtime) return null;

  const engineState = runtime.engine_state || "unknown";
  const cycleMs = runtime.engine_last_cycle_ms;
  const cycleCount = runtime.engine_cycle_count || 0;
  const heartbeatAt = runtime.engine_heartbeat_at;
  const lastError = runtime.engine_last_error;
  const lastRecoveryAt = runtime.engine_last_recovery_at;
  const heartbeatAgeS = heartbeatAt
    ? Math.max(0, (Date.now() - new Date(heartbeatAt).getTime()) / 1000)
    : Infinity;
  const recoveryAgeS = lastRecoveryAt
    ? Math.max(0, (Date.now() - new Date(lastRecoveryAt).getTime()) / 1000)
    : Infinity;
  // Show a transient "recovered" breadcrumb for 30s after a successful cycle
  // following a prior error — only when there is NO currently-active error.
  const showRecovered = !lastError && isFinite(recoveryAgeS) && recoveryAgeS <= 30;

  const meta = stateMeta({ engineState, cycleMs, heartbeatAgeS, lastError });
  const tone = TONE_CLASS[meta.tone];
  const showRestart = meta.tone === "red" || meta.label === "STALE" || meta.label === "BLOCKED" || meta.label === "SLEEPING";

  const formatAge = (s) => {
    if (!isFinite(s)) return "never";
    if (s < 60) return `${Math.round(s)}s ago`;
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    return `${Math.round(s / 3600)}h ago`;
  };

  return (
    <div
      data-testid="engine-health-card"
      className={`border rounded-sm p-4 ${tone.bg} ${tone.border}`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Activity size={16} strokeWidth={1.5} className={tone.text} />
          <div className="flex items-center gap-2 shrink-0">
            <span className="relative flex h-2 w-2">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${tone.dot} opacity-60`} />
              <span className={`relative inline-flex rounded-full h-2 w-2 ${tone.dot}`} />
            </span>
            <span className={`font-semibold tracking-wider text-xs uppercase ${tone.text}`} data-testid="engine-health-label">
              Engine · {meta.label}
            </span>
          </div>
          <div className="flex items-center gap-4 text-[11px] text-[#A0A0AB] font-mono min-w-0 flex-wrap">
            <span data-testid="engine-health-state">state <span className="text-white">{engineState}</span></span>
            <span data-testid="engine-health-cycle-ms">
              last cycle{" "}
              <span className="text-white">
                {cycleMs != null ? `${cycleMs}ms` : "—"}
              </span>
            </span>
            <span data-testid="engine-health-cycle-count">cycles <span className="text-white">{cycleCount}</span></span>
            <span data-testid="engine-health-heartbeat">
              last ok <span className="text-white">{heartbeatAt ? formatAge(heartbeatAgeS) : "never"}</span>
            </span>
          </div>
        </div>
        {showRestart ? (
          <Button
            data-testid="engine-health-restart"
            size="sm"
            variant="outline"
            onClick={restart}
            disabled={restarting}
            className={`rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633] text-xs h-7`}
          >
            <RefreshCw size={12} strokeWidth={1.8} className={`mr-1.5 ${restarting ? "animate-spin" : ""}`} />
            {restarting ? "Restarting…" : "Restart Engine"}
          </Button>
        ) : null}
      </div>

      {heartbeatAgeS > 120 && isFinite(heartbeatAgeS) ? (
        <div className="mt-2 flex items-start gap-2 text-[11px] text-[#F59E0B]" data-testid="engine-health-stale-warning">
          <AlertTriangle size={12} strokeWidth={1.5} className="shrink-0 mt-0.5" />
          <span>
            No successful cycle in {Math.round(heartbeatAgeS / 60)} minutes — the engine may be blocked.
          </span>
        </div>
      ) : null}

      {lastError ? (
        <div className="mt-2 flex items-start gap-2 text-[11px] text-[#FF3B30]" data-testid="engine-health-error">
          <AlertTriangle size={12} strokeWidth={1.5} className="shrink-0 mt-0.5" />
          <span className="font-mono truncate">
            {lastError.length > 220 ? `${lastError.slice(0, 220)}…` : lastError}
          </span>
        </div>
      ) : showRecovered ? (
        <div className="mt-2 flex items-start gap-2 text-[11px] text-[#00E676]" data-testid="engine-health-recovered">
          <Activity size={12} strokeWidth={1.5} className="shrink-0 mt-0.5" />
          <span>Recovered from error — engine healthy.</span>
        </div>
      ) : null}

      <span className="sr-only" data-testid="engine-health-tone">{meta.tone}</span>
      <span className="sr-only" data-testid="engine-health-last-success">{heartbeatAt || ""}</span>
    </div>
  );
}
